import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { SnackbarService } from '../../utilities/snackbar.service';
import { SKIP_ERROR_HANDLING_HEADER } from '../interceptors/api-interceptor.interceptor';

@Injectable({ providedIn: 'root' })
export class InternetStatusService {
  private readonly _isOnline = signal(true);
  private readonly reloadDelaySec: number = 10;
  private readonly lostMessageDurationSec: number = 10;
  public readonly isOnline = this._isOnline.asReadonly();
  public readonly snackbar = inject(SnackbarService);

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);

  private wasOffline = false;
  private reloadTimeout: ReturnType<typeof setTimeout> | null = null;

  public start(): void {
    window.addEventListener('online', this.onBrowserOnline);
    window.addEventListener('offline', this.onBrowserOffline);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('online', this.onBrowserOnline);
      window.removeEventListener('offline', this.onBrowserOffline);
    });

    if (!navigator.onLine) {
      this.confirmOffline();
    }

    effect(() => {
      const online = this._isOnline();

      if (!online) {
        this.wasOffline = true;
        this.clearRestartTimeout();
        this.snackbar.open(
          this.translate.instant('system.message.internetConnectionLost'),
          undefined,
          this.lostMessageDurationSec * 1000,
        );
      } else if (this.wasOffline) {
        const ref = this.snackbar.open(
          this.translate.instant('system.message.internetConnectionRestored', { seconds: this.reloadDelaySec }),
          undefined,
          0,
          this.translate.instant('button.cancel'),
        );
        ref.onAction().subscribe(() => this.clearRestartTimeout());

        this.ngZone.runOutsideAngular(() => {
          this.reloadTimeout = setTimeout(() => {
            location.reload();
          }, this.reloadDelaySec * 1000);
        });
      }
    });
  }

  private readonly onBrowserOnline = (): void => {
    this._isOnline.set(true);
  };

  private readonly onBrowserOffline = (): void => {
    this.confirmOffline();
  };

  private confirmOffline(): void {
    this.http
      .head('', {
        headers: { [SKIP_ERROR_HANDLING_HEADER]: 'true' },
        // ngsw-bypass is required: without it the service worker handles the request and its
        // safeFetch() turns a network failure into a synthetic 504, so status is never 0.
        params: { 'ngsw-bypass': 'true', probe: Date.now() },
      })
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => of(error.status !== 0)),
      )
      .subscribe((isReachable: boolean) => {
        if (!isReachable) {
          this._isOnline.set(false);
        }
      });
  }

  private clearRestartTimeout(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = null;
    }
  }
}
